import { useQuery } from "@tanstack/react-query";
import ordersClient, { Order } from "api/order";

export type DashboardOrder = {
  date: Date;
  isClockOrder: boolean;
  isCollectionOrder: boolean;
};

const PAGE_SIZE = 100;
const MAX_PAGES = 20;
/** Two full months back — enough for a month window plus its previous month. */
const HISTORY_MS = 62 * 24 * 60 * 60 * 1000;

/**
 * Recent (non-cancelled) orders from the MongoDB orders collection.
 * The admin search endpoint can't filter by date server-side, so we page
 * newest-first until we're past the history window.
 */
export default function useDashboardOrders() {
  return useQuery<DashboardOrder[]>({
    queryKey: ["dashboardOrders"],
    queryFn: async () => {
      const cutoff = Date.now() - HISTORY_MS;
      const orders: Order[] = [];
      for (let page = 0; page < MAX_PAGES; page++) {
        const batch = await ordersClient.textSearchOrders({
          skip: page * PAGE_SIZE,
          limit: PAGE_SIZE,
          sort: { orderCreationDate: -1 },
        });
        orders.push(...batch);
        if (batch.length < PAGE_SIZE) break;
        const oldest = batch[batch.length - 1].orderCreationDate;
        if (oldest && new Date(oldest).getTime() < cutoff) break;
      }
      return orders
        .filter((order) => !order.cancelled)
        .map((order) => ({
          date: new Date(order.orderCreationDate),
          isClockOrder:
            (order.clocksNumber ?? 0) > 0 || (order.clocks?.length ?? 0) > 0,
          isCollectionOrder: (order.collections?.length ?? 0) > 0,
        }))
        .filter((order) => !Number.isNaN(order.date.getTime()));
    },
    refetchInterval: 60_000,
  });
}
